import React, { useEffect, useRef, useState } from "react";

export interface PolylineProps extends google.maps.PolylineOptions {
  polylineData: string;
  onClick?: () => void;
  map?: google.maps.Map; // Add map to the interface
  tooltip?: string;
}

const XGSPolyline: React.FC<PolylineProps> = ({ map, polylineData, onClick, tooltip, ...props }) => {
  const [polyline, setPolyline] = useState<google.maps.Polyline>();
  const onClickListener = useRef<google.maps.MapsEventListener>();
  const infoWindow = useRef<google.maps.InfoWindow | null>(null);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (polyline) polyline.setMap(null);
      if (onClickListener.current) {
        google.maps.event.removeListener(onClickListener.current);
      }
      if (infoWindow.current) {
        infoWindow.current.close();
      }
    };
  }, [polyline]);

  // Update polyline options when props change
  useEffect(() => {
    if (polyline) polyline.setOptions(props);
  }, [polyline, props]);

  // Create/recreate polyline when polylineData changes
  useEffect(() => {
    if (!map) return; // Don't create polyline without map

    // Clean up existing polyline
    if (polyline) {
      polyline.setMap(null);
      if (onClickListener.current) {
        google.maps.event.removeListener(onClickListener.current);
        onClickListener.current = undefined;
      }
    }

    const decodedPath = window.google.maps.geometry.encoding.decodePath(polylineData);

    const newPolyline = new google.maps.Polyline({
      path: decodedPath,
      strokeColor: props?.strokeColor ?? "#000077",
      strokeOpacity: props?.strokeOpacity ?? 1,
      strokeWeight: props?.strokeWeight ?? 5,
      icons: props?.icons,
      clickable: true, 
      map: map, 
      ...props,
    });

    setPolyline(newPolyline);
    // eslint-disable-next-line
  }, [polylineData, map]); // Removed props from dependency array

  // Handle click events
  useEffect(() => {
    if (polyline && map) {
      // Clean up existing listener
      if (onClickListener.current) {
        google.maps.event.removeListener(onClickListener.current);
      }

      // Create InfoWindow if it doesn't exist
      if (!infoWindow.current) {
        infoWindow.current = new google.maps.InfoWindow({
          content: tooltip,
        });
      }

      onClickListener.current = google.maps.event.addListener(polyline, 'click', (event: any) => {
        if (infoWindow.current) {
          // Set the position to where the user clicked
          infoWindow.current.setPosition(event.latLng);
          // Open the info window on the map
          infoWindow.current.open(map);
        }

        // Call your original onClick handler if it exists
        if (onClick) {
          onClick();
        }
      });
    }
    // eslint-disable-next-line
  }, [polyline, map, tooltip]); // Removed onClick from dependency array to prevent recreation

  return null;
};

export default XGSPolyline;